// ── Is the system being updated, and is its API answering? (Bugify deploy-now, owner 2026-11-03) ───────────────────
// "注意部署时，增加等待进度条，确保客户看起来不是系统死机" — while a deploy restarts an installation, the staff and the
// clients on its portal must see that it is being updated, not that it died. Two signals, both cheap:
//   • build/deploy-status.json, written by deploy.sh on the server it deploys (utils/deployStatus.ts says how it is
//     read): every 30 s while the tab is in front, every 5 s while a deploy is on, at once when the tab comes back to the
//     front and at once on any API trouble.
//   • GET /api/health, probed every 3 s from the moment a request fails (utils/api.ts reportApiTrouble) — or, while the
//     file says the server is restarting, before anything has failed — until it answers again.
// components/DeployNotice.tsx draws what this holds. Module-level singleton with subscribe(), the same pattern as
// sandboxMode.ts: it outlives route changes and ends with the tab.
//
// ⛔ This file must not import utils/api.ts: api.ts imports it (reportApiTrouble), and a cycle between the two would
// hand one of them a half-built module at start-up. Hence ownApiBase() below, and the loaded build passed in by
// startApiHealth() rather than read from utils/appUpdate.tsx (which reaches the store, which reaches api.ts).
import { DeployStatus, ProbeState, PROBE_IDLE, parseDeployStatus, isNewBuild, probeTrouble, probeStep, DEPLOY_STATUS_MAX_AGE_MS } from '../utils/deployStatus';

export interface ApiHealth {
  probe: ProbeState;
  probeBase: string;             // the API host the last trouble was on
  status: DeployStatus;
  statusSeenAt: number;          // client ms of the last status file read (0 = none yet)
  busySeenAt: number;            // client ms at which a fresh deploying/restarting status was last seen (0 = never)
  busySince: number;             // server ms: when THIS deploy began (its first non-idle startedAt; 0 = idle). deploy.sh
                                 // stamps every write afresh, so "restarting" alone would restart the counter at 0:00
  restartBack: boolean;          // the API came back while the file still says the deploy is on (it is finishing)
  serverOffsetMs: number;        // server clock − client clock, from the status response's Date header
  newBuild: boolean;
  back: '' | 'overlay' | 'banner';   // "Back online" is showing, in the place the outage was shown
  dismissedOverlay: boolean;     // "Continue anyway" — the outage shows as a banner instead, until the API is back
  overlay: boolean;              // derived: the blocking "Updating — reconnecting" card is up (only for a deploy's outage)
}

const PROBE_FIRST_MS = 1000, PROBE_EVERY_MS = 3000, PROBE_TIMEOUT_MS = 5000;
const STATUS_EVERY_MS = 30_000, STATUS_BUSY_MS = 5000, BACK_SHOW_MS = 1500;

/** This build's own API host. The same expression as DEFAULT_BASE in utils/api.ts, which this file must not import
 *  (see the top). Only this host can raise the blocking card: a portal pinned to another installation cannot reach
 *  that one's /api/health from here when its CORS predates this feature, and must never be blocked by it. */
export const ownApiBase = (): string => process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

let state: ApiHealth = {
  probe: { ...PROBE_IDLE }, probeBase: '', status: { state: 'idle', build: '', startedAt: 0, stale: false },
  statusSeenAt: 0, busySeenAt: 0, busySince: 0, restartBack: false, serverOffsetMs: 0, newBuild: false, back: '', dismissedOverlay: false, overlay: false,
};
const subs = new Set<(s: ApiHealth) => void>();
let started = false;
let loadedBuild: () => string = () => '';
let chunkFailed = false;
let probeTimer: number | undefined;
let probing = false;             // a probe chain is scheduled or in flight
let probeGen = 0;                // bumped by every (re)schedule and stop: an answer from an older probe is dropped
let statusTimer: number | undefined;
let statusBusy = false;
let statusTriedAt = 0;           // client ms of the last status read started — failures come in bursts, reads need not
let statusUrl = '';              // the candidate that answered with a real v1 file, once one has
let backTimer: number | undefined;

/** A deploy seen this recently explains an outage: the card then says "Updating", and only then may it block. */
export const BUSY_FRESH_MS = 15 * 60_000;
/** Is this outage a deploy's? The server's status file says one is on now, or said so within BUSY_FRESH_MS. */
export const deployExplains = (s: ApiHealth, nowMs = Date.now()): boolean =>
  s.status.state !== 'idle' || (s.busySeenAt > 0 && nowMs - s.busySeenAt < BUSY_FRESH_MS);
/** The blocking card: this build's own API is down AND a deploy explains it (review round 2). A plain lost connection —
 *  a phone on a bad network, a client's Wi-Fi dropping — only gets the non-blocking "Reconnecting…" banner: making the
 *  chat, a half-written email or the portal unreachable on every network blip is not what the owner asked for. */
export const isOverlay = (s: ApiHealth): boolean => s.probe.down && s.probeBase === ownApiBase() && !s.dismissedOverlay && deployExplains(s);

function set(patch: Partial<ApiHealth>): void {
  const next = { ...state, ...patch };
  next.overlay = isOverlay(next) || next.back === 'overlay';
  state = next;
  subs.forEach(f => { try { f(state); } catch { /* a bad subscriber must not stop the others */ } });
}

export const getApiHealth = (): ApiHealth => state;

/** Subscribe to changes. Fires immediately with the current state; returns an unsubscribe. */
export function subscribeApiHealth(fn: (s: ApiHealth) => void): () => void {
  subs.add(fn); fn(state);
  return () => { subs.delete(fn); };
}

const reloadedFor = (): string => { try { return sessionStorage.getItem('slf_reloaded_for') || ''; } catch { return ''; } };
const computeNewBuild = (status: DeployStatus): boolean => chunkFailed || isNewBuild(status, loadedBuild(), reloadedFor());

// ── Probing /api/health ──────────────────────────────────────────────────────────────────────────────────────────────
async function healthOk(base: string): Promise<boolean> {
  const ctl = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const tm = window.setTimeout(() => ctl?.abort(), PROBE_TIMEOUT_MS);
  try {
    const res = await fetch(`${base}/health?t=${Date.now()}`, { cache: 'no-store', signal: ctl?.signal });
    return res.ok;
  } catch { return false; } finally { window.clearTimeout(tm); }
}

function scheduleProbe(ms: number): void {
  window.clearTimeout(probeTimer);
  probing = true;
  probeGen++;
  probeTimer = window.setTimeout(() => { void probeOnce(); }, ms);
}

function stopProbing(): void {
  window.clearTimeout(probeTimer);
  probeTimer = undefined;
  probing = false;
  probeGen++;
}

/** One answer about the API — a probe's, or a real request's (reportApiOk) — folded into the state. */
function applyProbe(ok: boolean): { state: ProbeState; emitBack: boolean; stop: boolean } {
  const now = Date.now();
  // A proactive probe (the file says "restarting") can find the outage before any request did: count it as trouble,
  // so the screens waiting on a request still hear "back" afterwards.
  const before = ok || state.probe.probing ? state.probe : probeTrouble(state.probe, now);
  const wasOverlay = isOverlay(state), wasDown = state.probe.down;
  const r = probeStep(before, ok, now);
  if (ok) {
    const back: ApiHealth['back'] = wasOverlay ? 'overlay' : wasDown ? 'banner' : '';
    set({ probe: r.state, back, dismissedOverlay: false, restartBack: state.restartBack || (wasDown && state.status.state !== 'idle') });
    if (back) {
      window.clearTimeout(backTimer);
      backTimer = window.setTimeout(() => set({ back: '' }), BACK_SHOW_MS);
    }
    if (r.emitBack || wasDown) {
      try { window.dispatchEvent(new Event('slf:api-back')); } catch { /* very old browser: the 5 s retries still run */ }
      void pollStatus();   // the new build is likely live now: its name is the reload offer
    }
  } else {
    set({ probe: r.state });
  }
  return r;
}

async function probeOnce(): Promise<void> {
  const gen = probeGen;
  const ok = await healthOk(state.probeBase || ownApiBase());
  if (gen !== probeGen) return;   // superseded meanwhile ("Try now", or a real request answered)
  const r = applyProbe(ok);
  // Keep watching while the file still says "restarting": the old process may still be the one answering.
  if (r.stop && state.status.state !== 'restarting') stopProbing();
  else scheduleProbe(PROBE_EVERY_MS);
}

function startProbing(base: string, first = PROBE_FIRST_MS): void {
  if (!state.probeBase || !probing) set({ probeBase: base });
  if (!probing) scheduleProbe(first);
}

/** A request to `base` failed the way a restarting server fails (utils/api.ts). Starts probing; never retries the
 *  request itself — the screens that care listen for slf:api-back. */
export function reportApiTrouble(base: string): void {
  const b = base || ownApiBase();
  set({ probe: probeTrouble(state.probe, Date.now()), probeBase: b });
  if (!probing) scheduleProbe(PROBE_FIRST_MS);
  if (started && Date.now() - statusTriedAt > PROBE_EVERY_MS) void pollStatus();
}

/** A request to `base` was answered (utils/api.ts) — any answer but a proxy's 502/503/504: the host is reachable, so
 *  the trouble is over whatever /api/health says. This is also what ends it for a portal pinned to an installation
 *  whose /api/health this page's origin may not read (an older CORS rule), where probing alone would never succeed. */
export function reportApiOk(base: string): void {
  if (!state.probe.probing || (base || ownApiBase()) !== state.probeBase) return;
  applyProbe(true);
  if (state.status.state !== 'restarting') stopProbing();
}

/** "Try now" on the card: probe at once instead of waiting for the next tick. */
export function probeNow(): void {
  if (!state.probe.probing && !probing) return;
  scheduleProbe(0);
}

/** "Continue anyway": the outage goes on showing, as a banner that blocks nothing, until the API is back. */
export function dismissOverlay(): void { set({ dismissedOverlay: true }); }

// ── The status file ──────────────────────────────────────────────────────────────────────────────────────────────────
const isV1 = (text: string): boolean => {
  try { const o = JSON.parse(text); return !!o && typeof o === 'object' && o.v === 1; } catch { return false; }
};

function candidates(): string[] {
  if (statusUrl) return [statusUrl];
  const list = ['/deploy-status.json'];
  // A portal served under /portal/ on another host (the firm's website) maps its own copy there (ops/nginx-site.conf).
  try { if (window.location.pathname.indexOf('/portal') === 0) list.push('/portal/deploy-status.json'); } catch { /* no DOM */ }
  return list;
}

function scheduleStatus(): void {
  window.clearTimeout(statusTimer);
  statusTimer = window.setTimeout(() => { void pollStatus(); }, state.status.state === 'idle' ? STATUS_EVERY_MS : STATUS_BUSY_MS);
}

async function pollStatus(): Promise<void> {
  if (statusBusy) return;
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') { window.clearTimeout(statusTimer); return; }
  statusBusy = true;
  statusTriedAt = Date.now();
  try {
    let found: { text: string; date: string | null; modified: string | null; url: string } | null = null;
    let reached = false;
    for (const url of candidates()) {
      try {
        const res = await fetch(`${url}?t=${Date.now()}`, { cache: 'no-store' });
        reached = true;
        if (!res.ok) continue;
        const text = await res.text();
        if (!isV1(text)) continue;
        found = { text, date: res.headers.get('Date'), modified: res.headers.get('Last-Modified'), url };
        break;
      } catch { /* the host itself is unreachable: keep what was last known */ }
    }
    if (!reached) return;
    if (found && !statusUrl) statusUrl = found.url;
    const now = Date.now();
    const status = found ? parseDeployStatus(found.text, { date: found.date, lastModified: found.modified }, now) : parseDeployStatus('', {}, now);
    const dateMs = found?.date ? Date.parse(found.date) : NaN;
    const busy = status.state !== 'idle';
    // One deploy = one counter: "deploying" then "restarting" keep the first startedAt (a gap longer than a deploy's
    // own lifetime is a new deploy, seen by a tab that slept through the idle in between).
    const prevSince = state.status.state !== 'idle' ? state.busySince : 0;
    const busySince = !busy ? 0
      : prevSince && (!status.startedAt || (status.startedAt >= prevSince && status.startedAt - prevSince < DEPLOY_STATUS_MAX_AGE_MS)) ? prevSince
        : status.startedAt;
    set({
      status, statusSeenAt: now, newBuild: computeNewBuild(status),
      busySince, restartBack: busy && busySince === prevSince ? state.restartBack : false,
      busySeenAt: busy ? now : state.busySeenAt,
      serverOffsetMs: isFinite(dateMs) ? dateMs - now : state.serverOffsetMs,
    });
    // The server is about to drop its connections: watch its own API before anything fails.
    if (status.state === 'restarting') startProbing(ownApiBase(), 0);
  } finally {
    statusBusy = false;
    scheduleStatus();
  }
}

// ── Start-up ─────────────────────────────────────────────────────────────────────────────────────────────────────────
const isStaticAsset = (el: any): boolean => {
  const tag = String(el?.tagName || '');
  const url = tag === 'SCRIPT' ? String(el.src || '') : tag === 'LINK' ? String(el.href || '') : '';
  return !!url && url.indexOf('/static/') >= 0;
};

function onChunkFailed(): void {
  if (chunkFailed) return;
  chunkFailed = true;
  set({ newBuild: true });
}

/** Idempotent. DeployNotice calls it on mount with the build this page loaded (utils/appUpdate.tsx loadedBuild). */
export function startApiHealth(loaded?: () => string): void {
  if (loaded) loadedBuild = loaded;
  if (started) return;
  started = true;
  try {
    // A lazily loaded piece of the old build that the deploy has removed: only a reload brings the new one.
    window.addEventListener('error', e => { if (isStaticAsset(e.target)) onChunkFailed(); }, true);
    window.addEventListener('unhandledrejection', e => { if ((e as PromiseRejectionEvent)?.reason?.name === 'ChunkLoadError') onChunkFailed(); });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') return;
      void pollStatus();
      if (state.probe.probing) probeNow();
    });
  } catch { /* no DOM */ }
  void pollStatus();
}
